import { cssSpacing, cssPixels, typography, drawerDefaults, spacing } from "../../../app/design-system";
import { DeleteOutlined,PlusOutlined } from "@ant-design/icons";
import { Button,Col,Divider,Flex,Form,Input,InputNumber,Row,Select,Space,Tooltip,Typography } from "../../../../../../../shared/frontend/ui";
import dayjs,{ Dayjs } from "dayjs";
import React from "react";
import { useFinanceOperation } from "../../../app/operations/useFinanceOperation";
import { useDirtySnapshot } from "../../../../../../../shared/frontend/forms/useDirtySnapshot";
import type { ApiClient } from "../../../app/api/client";
import type { StudioWithAccess } from "../../../app/api/types";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatMoney } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { articleAppliesToSaleStudio } from "../../../app/utils/articleStudioScope";
import { useLastPaymentMethod } from "../../../app/prefs/useLastPaymentMethod";
import { paymentMethodDefinitionSelectOptions } from "../../../app/utils/paymentMethod";
import { PAYMENT_METHOD_SEPA } from "../../../app/utils/paymentMethodCodes";
import { FormDrawer } from "../../../components/FormDrawer";
import { FormSectionHeading } from "../../../components/FormSectionHeading";
import type { PersonDto } from "../types";
import { loadPersonDetailRecord } from "./usePersonDetailRecord";
import { DatePicker } from "../../../../../../../shared/frontend/components/ControlledPickers";
import type { Feedback } from "../../../../../../../shared/frontend/feedback/useFeedback";

type ArticleDto = {
  id: string;
  name: string;
  sku?: string | null;
  unitPriceGross: number;
  allowPriceOverrideOnSale?: boolean;
  isActive: boolean;
  studioIds?: string[];
};

type PaymentMethodDefinitionDto = {
  id: string;
  paymentMethodType: number;
  name: string;
  isActive: boolean;
};
type SepaGroupDto = { id: string; name: string; isActive: boolean };
type SepaGroupSuggestionDto = { sepaGroupId?: string | null };

type DraftRow = {
  articleId: string;
  quantity: number;
  unitPriceGross?: number;
};

type Props = {
  open: boolean;
  api: Pick<ApiClient, "get" | "post" | "req">;
  message: Feedback;
  text: I18nContextValue["text"];
  units: UnitsSettings;
  personId: string;
  homeStudioId?: string | null;
  allowedStudios: StudioWithAccess[];
  dateInputFormat: string | string[];
  decimalSeparator: string;
  currencyCode: string;
  moneyPlaceholder: string;
  onClose: () => void;
  onPersonUpdated: (person: PersonDto) => void;
  onSaved?: () => void;
};

export function PersonArticleSaleDrawer({
  open,
  api,
  message,
  text,
  units,
  personId,
  homeStudioId,
  allowedStudios,
  dateInputFormat,
  decimalSeparator,
  currencyCode,
  moneyPlaceholder,
  onClose,
  onPersonUpdated,
  onSaved
}: Props) {
  const operation = useFinanceOperation(personId);
  const saving = operation.pending;
  const [articles, setArticles] = React.useState<ArticleDto[]>([]);
  const [rows, setRows] = React.useState<DraftRow[]>([]);
  const [saleDate, setSaleDate] = React.useState<Dayjs>(() => dayjs());
  const [studioId, setStudioId] = React.useState<string | undefined>(undefined);
  const [paymentMethods, setPaymentMethods] = React.useState<PaymentMethodDefinitionDto[]>([]);
  const [paymentMethod, setPaymentMethod] = React.useState<number | undefined>(undefined);
  const [sepaGroups, setSepaGroups] = React.useState<SepaGroupDto[]>([]);
  const [sepaGroupId, setSepaGroupId] = React.useState<string | undefined>(undefined);
  const saleDraft = { rows, saleDate: saleDate.format("YYYY-MM-DD"), studioId, paymentMethod, sepaGroupId };
  const { dirty, markSaved } = useDirtySnapshot(saleDraft);
  const baselineOpened = React.useRef(false);
  const initializeSaleDraft = React.useEffectEvent(() => { markSaved(saleDraft); baselineOpened.current = true; });
  const applySuggestedGroup = React.useEffectEvent((value: string | undefined) => {
    setSepaGroupId(value);
    if (!dirty) markSaved({ ...saleDraft, sepaGroupId: value });
  });

  // Artikel, Zahlarten und SEPA-Gruppen füllen die Auswahlfelder des Verkaufs. Ohne
  // Ladekennzeichen stand die Artikelliste beim Öffnen leer da und wirkte wie „keine Artikel".
  const [lookupsLoading, setLookupsLoading] = React.useState(false);
  React.useEffect(() => {
    if (!open) return;
    const preferredStudioId = homeStudioId && allowedStudios.some((studio) => String(studio.id) === String(homeStudioId))
      ? homeStudioId
      : allowedStudios.length === 1 ? allowedStudios[0]?.id : undefined;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Beim Öffnen wird der neue Verkaufsentwurf gezielt mit dem Stammstudio vorbelegt.
    setStudioId(preferredStudioId);
    let alive = true;
    setLookupsLoading(true);
    void Promise.all([
      api
        .get<ArticleDto[]>("/api/v1/articles")
        .then((list) => {
          if (alive) setArticles((list || []).filter((x) => x.isActive !== false));
        })
        .catch(() => {}),
      api
        .get<PaymentMethodDefinitionDto[]>("/api/v1/payment-method-definitions")
        .then((pm) => { if (alive) setPaymentMethods((pm || []).filter((x) => x.isActive)); })
        .catch(() => { if (alive) setPaymentMethods([]); }),
      api.get<SepaGroupDto[]>("/api/v1/sepa-groups")
        .then((groups) => { if (alive) setSepaGroups((groups || []).filter((group) => group.isActive)); })
        .catch(() => { if (alive) setSepaGroups([]); })
    ]).finally(() => { if (alive) setLookupsLoading(false); });
    return () => { alive = false; };
  }, [allowedStudios, api, homeStudioId, open]);

  const paymentMethodOptions = React.useMemo(
    () => paymentMethodDefinitionSelectOptions(paymentMethods),
    [paymentMethods]
  );
  const { rememberPaymentMethod, resolveDefaultPaymentMethod } = useLastPaymentMethod();

  /**
   * Zahlungsart des Verkaufs: die eigene Auswahl, sonst die zuletzt selbst erfasste Zahlungsart.
   * Abgeleitet statt gesetzt — die Zahlarten kommen asynchron, ein Zustandswechsel im Effekt löste
   * nur eine zweite Renderrunde aus.
   */
  const effectivePaymentMethod =
    paymentMethod ?? resolveDefaultPaymentMethod(paymentMethodOptions, null) ?? undefined;

  React.useEffect(() => {
    if (!open || effectivePaymentMethod !== PAYMENT_METHOD_SEPA) return;
    let active = true;
    api.get<SepaGroupSuggestionDto>(`/api/v1/article-sales/sepa-group-suggestion?personId=${encodeURIComponent(personId)}`)
      .then((suggestion) => { if (active) applySuggestedGroup(suggestion?.sepaGroupId || undefined); })
      .catch(() => { if (active) applySuggestedGroup(undefined); });
    return () => { active = false; };
  }, [api, effectivePaymentMethod, open, personId]);

  const saleStudioFilterId = studioId ?? (allowedStudios.length === 1 ? allowedStudios[0]?.id : undefined);

  const articleOptions = React.useMemo(
    () =>
      (articles || [])
        .filter((a) => a.isActive !== false && articleAppliesToSaleStudio(a, saleStudioFilterId))
        .map((a) => ({ value: String(a.id), label: `${a.name}${a.sku ? ` (${a.sku})` : ""}` })),
    [articles, saleStudioFilterId]
  );

  const articleById = React.useMemo(
    () => new Map((articles || []).map((a) => [String(a.id), a])),
    [articles]
  );

  const saleTotal = React.useMemo(
    () =>
      (rows || []).reduce((sum, r) => {
        const article = articleById.get(String(r.articleId));
        const unitPrice = Number(r.unitPriceGross ?? article?.unitPriceGross ?? 0);
        return sum + unitPrice * Number(r.quantity || 0);
      }, 0),
    [articleById, rows]
  );

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Das asynchrone Artikelladen synchronisiert den Entwurf mit dem aktuell zulässigen Sortiment.
    setRows((prev) => {
      if (!articleOptions.length) return [];
      const allowed = new Set(articleOptions.map((o) => o.value));
      const next = prev
        .map((r) => {
          const articleId = allowed.has(r.articleId) ? r.articleId : String(articleOptions[0].value);
          const article = articleById.get(articleId);
          return {
            ...r,
            articleId,
            unitPriceGross: r.unitPriceGross ?? Number(article?.unitPriceGross || 0)
          };
        })
        .filter((r) => allowed.has(r.articleId));
      if (next.length === 0) {
        const articleId = String(articleOptions[0].value);
        const article = articleById.get(articleId);
        return [{ articleId, quantity: 1, unitPriceGross: Number(article?.unitPriceGross || 0) }];
      }
      return next;
    });
  }, [articleById, articleOptions]);

  const submitSell = React.useCallback(async () => {
    const clean = (rows || []).filter((r) => String(r.articleId).trim() && Number(r.quantity || 0) > 0);
    if (clean.length === 0) {
      message.error(text("Please add at least one article."));
      return;
    }
    if (effectivePaymentMethod == null || Number.isNaN(effectivePaymentMethod)) {
      message.error(text("Please select a payment method."));
      return;
    }

    try {
      const result = await operation.execute(async (committed) => {
        const effectiveStudioId = studioId ?? homeStudioId ?? (allowedStudios.length === 1 ? allowedStudios[0].id : undefined);
        const body: Record<string, unknown> = {
          personId,
          saleDate: saleDate.format("YYYY-MM-DD"),
          sourceType: 1,
          paymentMethod: effectivePaymentMethod,
          items: clean.map((r) => ({
            articleId: r.articleId,
            quantity: Number(r.quantity || 1),
            unitPriceGross: Number(r.unitPriceGross || 0)
          }))
        };
        if (effectiveStudioId) body.studioId = effectiveStudioId;
        if (effectivePaymentMethod === PAYMENT_METHOD_SEPA && sepaGroupId) body.sepaGroupId = sepaGroupId;
        await api.post("/api/v1/article-sales", body);
        committed();
        rememberPaymentMethod(effectivePaymentMethod);
        const updatedPerson = await loadPersonDetailRecord(api, personId).catch(() => null);
        if (updatedPerson) onPersonUpdated(updatedPerson);
        message.success(text("Saved."));
        setRows(articleOptions.length ? [{ articleId: String(articleOptions[0].value), quantity: 1, unitPriceGross: Number(articleById.get(String(articleOptions[0].value))?.unitPriceGross || 0) }] : []);
        onClose();
        onSaved?.();
      });
      if (result === "writeUncertain") onClose();
    } catch {
      message.error(text("Failed to save"));
    }
  }, [allowedStudios, api, articleById, articleOptions, homeStudioId, message, onClose, onPersonUpdated, onSaved, effectivePaymentMethod, personId, rememberPaymentMethod, rows, saleDate, sepaGroupId, studioId, text, operation]);

  React.useEffect(() => {
    if (!open) { baselineOpened.current = false; return; }
    if (!baselineOpened.current && !lookupsLoading && rows.length > 0) initializeSaleDraft();
  }, [open, lookupsLoading, rows.length]);
  return (
    <FormDrawer mode="edit"
      open={open}
      dirty={dirty}
      title="Sell articles"
      width={drawerDefaults.width}
      onCancel={() => {
        if (saving) return;
        onClose();
      }}
      submitting={saving}
      contentLoading={lookupsLoading}
      onSave={() => submitSell()}
    >
      <Form layout="vertical" requiredMark={false}>
        {allowedStudios.length > 1 ? (
          <Form.Item label={text("Studio")}>
            <Select
              allowClear
              placeholder={text("Studio")}
              style={{ width: "100%" }}
              value={studioId}
              onChange={(v) => setStudioId(v || undefined)}
              options={allowedStudios.map((s) => ({ value: s.id, label: s.name }))}
            />
          </Form.Item>
        ) : null}

        <Form.Item label={text("Sale date")}>
          <DatePicker
            value={saleDate}
            onChange={(d) => setSaleDate(d || dayjs())}
            format={dateInputFormat}
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item label={text("Payment method")} required>
          <Select
            placeholder={text("Payment method")}
            style={{ width: "100%" }}
            value={effectivePaymentMethod}
            onChange={(value) => setPaymentMethod(value != null ? Number(value) : undefined)}
            options={paymentMethodOptions}
          />
        </Form.Item>
        {effectivePaymentMethod === PAYMENT_METHOD_SEPA ? (
          <Form.Item label={text("Direct debit group")}>
            <Select allowClear placeholder={text("Direct debit group")} value={sepaGroupId} onChange={(value) => setSepaGroupId(value || undefined)} options={sepaGroups.map((group) => ({ value: group.id, label: group.name }))} />
          </Form.Item>
        ) : null}

        <FormSectionHeading title={text("Articles")} sectionKey="person-article-sale.articles">
          {articleOptions.length === 0 ? (
            <Typography.Text type="secondary">{text("No matches")}</Typography.Text>
          ) : (
            <>
              <Flex vertical gap={6} style={{ width: "100%" }}>
                {rows.map((r, idx) => (
                  <Row key={`${r.articleId}-${idx}`} gutter={12} align="bottom" wrap>
                    {(() => {
                      const article = articleById.get(String(r.articleId));
                      const unitPrice = Number(r.unitPriceGross ?? article?.unitPriceGross ?? 0);
                      const priceEditable = article?.allowPriceOverrideOnSale !== false;
                      const lineTotal = unitPrice * Number(r.quantity || 0);
                      return (
                        <>
                          <Col xs={24} md={10}>
                            <Form.Item label={idx === 0 ? text("Article") : " "} style={{ marginBottom: 0 }}>
                              <Select
                                style={{ width: "100%" }}
                                value={r.articleId}
                                onChange={(v) => setRows((prev) => prev.map((x, i) => {
                                  if (i !== idx) return x;
                                  const nextArticleId = String(v);
                                  const nextArticle = articleById.get(nextArticleId);
                                  return { ...x, articleId: nextArticleId, unitPriceGross: Number(nextArticle?.unitPriceGross || 0) };
                                }))}
                                options={articleOptions}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={8} md={4}>
                            <Form.Item label={idx === 0 ? text("Quantity") : " "} style={{ marginBottom: 0 }}>
                              <InputNumber
                                min={1}
                                value={r.quantity}
                                style={{ width: "100%" }}
                                onChange={(v) => setRows((prev) => prev.map((x, i) => (i === idx ? { ...x, quantity: Number(v || 1) } : x)))}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={8} md={4}>
                            <Form.Item label={idx === 0 ? text("Price") : " "} style={{ marginBottom: 0 }}>
                              <Space.Compact style={{ width: "100%", display: "flex" }}>
                                <InputNumber
                                  min={0}
                                  step={0.01}
                                  value={unitPrice}
                                  disabled={!priceEditable}
                                  decimalSeparator={decimalSeparator}
                                  placeholder={moneyPlaceholder}
                                  style={{ flex: 1, minWidth: 0 }}
                                  onChange={(v) => setRows((prev) => prev.map((x, i) => (i === idx ? { ...x, unitPriceGross: Number(v || 0) } : x)))}
                                />
                                <Input
                                  aria-hidden="true"
                                  readOnly
                                  tabIndex={-1}
                                  value={currencyCode}
                                  style={{ width: 64, textAlign: "center", pointerEvents: "none" }}
                                />
                              </Space.Compact>
                            </Form.Item>
                          </Col>
                          <Col xs={16} sm={6} md={4}>
                            <Form.Item label={idx === 0 ? text("Total") : " "} style={{ marginBottom: 0 }}>
                              <Typography.Text strong style={{ display: "block", minHeight: 32, lineHeight: cssPixels(typography.fontSize.xxl), textAlign: "right" }}>
                                {formatMoney(lineTotal, units)}
                              </Typography.Text>
                            </Form.Item>
                          </Col>
                          <Col xs={8} sm={2} md={2}>
                            <Form.Item label=" " style={{ marginBottom: 0 }}>
                              <Tooltip title={text("Delete")}>
                                <Button
                                  danger
                                  icon={<DeleteOutlined />}
                                  aria-label={text("Delete")}
                                  onClick={() => setRows((prev) => prev.filter((_, i) => i !== idx))}
                                  style={{ width: "100%" }}
                                />
                              </Tooltip>
                            </Form.Item>
                          </Col>
                        </>
                      );
                    })()}
                  </Row>
                ))}
              </Flex>

              <Divider style={{ margin: cssSpacing(spacing.controlGap, 0, spacing.controlGap) }} />

              <Flex justify="space-between" align="center" gap={12} wrap="wrap">
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const articleId = String(articleOptions[0].value);
                    const article = articleById.get(articleId);
                    setRows((prev) => [...prev, { articleId, quantity: 1, unitPriceGross: Number(article?.unitPriceGross || 0) }]);
                  }}
                >
                  {text("Add article")}
                </Button>
                <Space>
                  <Typography.Text type="secondary">{text("Total")}</Typography.Text>
                  <Typography.Text strong>{formatMoney(saleTotal, units)}</Typography.Text>
                </Space>
              </Flex>
            </>
          )}
        </FormSectionHeading>
      </Form>
    </FormDrawer>
  );
}
